import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Conversation,
  ConversationConductor,
  CONVERSATION_CONDUCTOR_LABELS,
} from '../../../types/conversation';
import { SupportGroup } from '../../../types/supportGroup';
import {
  CHANNEL_TYPE_LABELS,
  ChannelType,
  CONVERSATION_STATUS_COLORS,
  CONVERSATION_STATUS_LABELS,
  ConversationStatus,
  isConversationClosedForMessaging,
  SupportMessageStatus,
} from '../../../types/atendimentoEnums';
import { InboxFormBinding } from '../../../types/inbox';
import { conversationService } from '../../../services/conversationService';
import { inboxService } from '../../../services/inboxService';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { parseSupportMessageDelivery } from '../../../utils/supportMessageDelivery';
import { Button } from '../../UI/Button';
import { Modal } from '../../UI/Modal';
import { useToast } from '../../UI/ToastProvider';
import { WorkspaceThemeClasses } from './workspaceTheme';
import { ClassificationBadges } from './ClassificationBadges';
import { ClassificationBadgeDisplay } from '../../../utils/classificationBadgeDisplay';

interface WorkspaceChatActionsProps {
  theme: WorkspaceThemeClasses;
  conversation: Conversation;
  groups: SupportGroup[];
  selectedGroupId: string;
  actionLoading: boolean;
  conductor: ConversationConductor | null;
  canAssume: boolean;
  canHandoffToHuman: boolean;
  canHandoffToBot: boolean;
  canCapture: boolean;
  canReopen: boolean;
  classificationBadges?: ClassificationBadgeDisplay[];
  emailSubject?: string | null;
  onSelectedGroupChange: (groupId: string) => void;
  onTransferGroup: () => void;
  onAssume: () => void;
  onHandoffToHuman: () => void;
  onHandoffToBot: () => void;
  onUpdateStatus: (status: ConversationStatus) => void;
  onResolve: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onShowHistory: () => void;
  contactPanelOpen: boolean;
  onToggleContactPanel: () => void;
  onBackToQueue?: () => void;
}

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '?';
  }

  if (parts.length === 1)
  {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const conductorTone: Record<ConversationConductor, string> = {
  bot: 'bg-purple-100 text-purple-800',
  human: 'bg-blue-100 text-blue-800',
  waiting: 'bg-orange-100 text-orange-800',
};

export const WorkspaceChatActions: React.FC<WorkspaceChatActionsProps> = ({
  theme,
  conversation,
  groups,
  selectedGroupId,
  actionLoading,
  conductor,
  canAssume,
  canHandoffToHuman,
  canHandoffToBot,
  canCapture,
  canReopen,
  classificationBadges = [],
  emailSubject,
  onSelectedGroupChange,
  onTransferGroup,
  onAssume,
  onHandoffToHuman,
  onHandoffToBot,
  onUpdateStatus,
  onResolve,
  onReopen,
  onDelete,
  onMarkRead,
  onMarkUnread,
  onShowHistory,
  contactPanelOpen,
  onToggleContactPanel,
  onBackToQueue,
}) => {
  const { addToast } = useToast();
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPresentFormOpen, setIsPresentFormOpen] = useState(false);
  const [linkedForms, setLinkedForms] = useState<InboxFormBinding[]>([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [presentingForm, setPresentingForm] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const isWidgetConversation = conversation.channelType === ChannelType.WebWidget;
  const canPresentForm = !isConversationClosedForMessaging(conversation.status);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node))
      {
        setShowActionsMenu(false);
      }

      if (statusRef.current && !statusRef.current.contains(event.target as Node))
      {
        setShowStatusMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const ghostBtn = `text-xs font-semibold px-2.5 py-1.5 rounded-md border ${theme.border} ${theme.muted} disabled:opacity-50 hover:bg-opacity-10`;
  const primaryBtn = `text-xs font-semibold px-2.5 py-1.5 rounded-md ${theme.accentBtn} disabled:opacity-50`;

  return (
    <div className={`flex-shrink-0 border-b ${theme.surface} ${theme.border}`}>
      <div className="px-4 pt-3 pb-2">
        <div className="flex flex-wrap items-start gap-2 justify-between">
          <div className="min-w-0 flex items-start gap-1">
            {onBackToQueue && (
              <button
                type="button"
                onClick={onBackToQueue}
                className={`lg:hidden mt-1 flex-shrink-0 rounded-md p-1.5 ${theme.muted} hover:bg-gray-100`}
                aria-label="Voltar para a fila"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0">
            <button
              type="button"
              onClick={onToggleContactPanel}
              data-contact-panel-toggle=""
              aria-expanded={contactPanelOpen}
              aria-controls="workspace-contact-panel"
              title={contactPanelOpen ? 'Ocultar informações do contato' : 'Ver informações do contato'}
              className={`group flex max-w-full items-center gap-2.5 rounded-lg px-1.5 py-1 -ml-1.5 text-left transition-colors ${
                contactPanelOpen ? theme.accentSoft : 'hover:bg-gray-100'
              }`}
            >
              <span className="h-9 w-9 flex-shrink-0 rounded-full bg-blue-600 text-white text-xs font-bold grid place-items-center">
                {initialsFromName(conversation.contactName)}
              </span>
              <span className="min-w-0">
                <span className={`flex items-center gap-1 text-sm font-bold ${theme.text}`}>
                  <span className="truncate">
                    #{conversation.displayId} — {conversation.contactName}
                  </span>
                  <ChevronRightIcon
                    className={`h-4 w-4 flex-shrink-0 transition-transform ${theme.muted} ${
                      contactPanelOpen ? 'rotate-90' : 'group-hover:translate-x-0.5'
                    }`}
                    aria-hidden="true"
                  />
                </span>
                {emailSubject && (
                  <span className={`mt-0.5 block truncate text-[13px] font-medium leading-snug ${theme.text}`} title={emailSubject}>
                    {emailSubject}
                  </span>
                )}
                <span className={`mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs ${theme.muted}`}>
                  <span>{CHANNEL_TYPE_LABELS[conversation.channelType]}</span>
                  <span>·</span>
                  <span>{conversation.inboxName}</span>
                  <span>·</span>
                  <span>{conversation.supportGroupName || 'Sem grupo'}</span>
                  <span>·</span>
                  <span>{conversation.assigneeName || 'Sem agente'}</span>
                </span>
              </span>
            </button>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {conductor && !isConversationClosedForMessaging(conversation.status) && (
                <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full ${conductorTone[conductor]}`}>
                  {CONVERSATION_CONDUCTOR_LABELS[conductor]}
                </span>
              )}
              {!(
                conductor === 'waiting' &&
                conversation.status === ConversationStatus.WaitingForOperator
              ) && (
                <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full ${CONVERSATION_STATUS_COLORS[conversation.status]}`}>
                  {CONVERSATION_STATUS_LABELS[conversation.status]}
                </span>
              )}
              {classificationBadges.length > 0 && (
                <ClassificationBadges badges={classificationBadges} />
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`px-4 pb-3 flex flex-wrap items-center gap-2`}>
        <div className="flex items-center gap-1.5">
          <select
            value={selectedGroupId}
            onChange={(event) => onSelectedGroupChange(event.target.value)}
            disabled={actionLoading}
            className={`text-xs rounded-md border px-2 py-1.5 max-w-[160px] ${theme.input}`}
            aria-label="Transferir para grupo"
          >
            <option value="">Transferir para grupo…</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onTransferGroup}
            disabled={actionLoading || !selectedGroupId}
            className={ghostBtn}
          >
            Transferir
          </button>
        </div>

        <button
          type="button"
          onClick={onAssume}
          disabled={actionLoading || !canAssume}
          className={ghostBtn}
          title={!canAssume ? 'Disponível quando a conversa aguarda operador ou está com o bot' : undefined}
        >
          Assumir
        </button>

        <div className="relative" ref={actionsRef}>
          <button
            type="button"
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            disabled={actionLoading}
            className={ghostBtn}
          >
            Ações
            <svg className="inline-block ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showActionsMenu && (
            <div className={`absolute left-0 top-full mt-1 rounded-md shadow-lg border z-10 ${theme.surface} ${theme.border} min-w-[180px]`}>
              {canPresentForm && (
                <button
                  type="button"
                  onClick={async () => {
                    setShowActionsMenu(false);
                    try
                    {
                      const forms = await inboxService.getForms(conversation.inboxId);
                      const presentable = forms.filter((item) => item.formAccessType !== 'Private');
                      setLinkedForms(forms);
                      setSelectedFormId(presentable[0]?.formId ?? '');
                      setIsPresentFormOpen(true);
                    }
                    catch (error)
                    {
                      addToast(apiErrorMessage(error, 'Não foi possível carregar os formulários'), 'error');
                    }
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Enviar formulário
                </button>
              )}
              {canHandoffToHuman && (
                <button
                  type="button"
                  onClick={() => {
                    onHandoffToHuman();
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Encaminhar p/ humano
                </button>
              )}
              {canHandoffToBot && (
                <button
                  type="button"
                  onClick={() => {
                    onHandoffToBot();
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Devolver ao bot
                </button>
              )}
              {canCapture && (
                <button
                  type="button"
                  onClick={() => {
                    onUpdateStatus(ConversationStatus.Captured);
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Capturar
                </button>
              )}
              {canReopen && (
                <button
                  type="button"
                  onClick={() => {
                    onReopen();
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Reabrir
                </button>
              )}
              {(conversation.unreadCount ?? 0) > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    onMarkRead();
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Marcar como lida
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onMarkUnread();
                    setShowActionsMenu(false);
                  }}
                  disabled={actionLoading}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
                >
                  Marcar como não lida
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowActionsMenu(false);
                  setIsDeleteModalOpen(true);
                }}
                disabled={actionLoading}
                className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Excluir conversa
              </button>
            </div>
          )}
        </div>

        <span className={`h-4 w-px mx-0.5 ${theme.border} bg-current opacity-20`} />

        <div className="relative" ref={statusRef}>
          <button
            type="button"
            onClick={() => setShowStatusMenu(!showStatusMenu)}
            disabled={actionLoading}
            className={ghostBtn}
          >
            Alterar status
            <svg className="inline-block ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showStatusMenu && (
            <div className={`absolute left-0 top-full mt-1 rounded-md shadow-lg border z-10 ${theme.surface} ${theme.border} min-w-[160px]`}>
              <button
                type="button"
                onClick={() => {
                  onUpdateStatus(ConversationStatus.Pending);
                  setShowStatusMenu(false);
                }}
                disabled={actionLoading}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
              >
                Pendente
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdateStatus(ConversationStatus.WaitingForOperator);
                  setShowStatusMenu(false);
                }}
                disabled={actionLoading}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
              >
                Aguardando
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdateStatus(ConversationStatus.InProgress);
                  setShowStatusMenu(false);
                }}
                disabled={actionLoading}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-opacity-10 ${theme.text} disabled:opacity-50`}
              >
                Em atendimento
              </button>
            </div>
          )}
        </div>

        {conversation.status === ConversationStatus.Resolved ? (
          <button
            type="button"
            onClick={onReopen}
            disabled={actionLoading}
            className={primaryBtn}
          >
            Reabrir
          </button>
        ) : (
          <button
            type="button"
            onClick={onResolve}
            disabled={actionLoading}
            className={primaryBtn}
          >
            Finalizar
          </button>
        )}

        <span className={`h-4 w-px mx-0.5 ${theme.border} bg-current opacity-20`} />

        <button
          type="button"
          onClick={onShowHistory}
          disabled={actionLoading}
          className={ghostBtn}
          title="Ver histórico de ações"
        >
          <svg className="inline-block h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="ml-1">Histórico</span>
        </button>
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        title="Excluir conversa"
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <p className="text-gray-600 mb-4">
          Deseja excluir a conversa <strong>#{conversation.displayId}</strong> de{' '}
          <strong>{conversation.contactName}</strong>? Esta ação remove o histórico de mensagens e não pode ser desfeita.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={actionLoading}
            onClick={() => {
              setIsDeleteModalOpen(false);
              onDelete();
            }}
          >
            Excluir
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isPresentFormOpen}
        title="Enviar formulário"
        onClose={() => setIsPresentFormOpen(false)}
      >
        {linkedForms.filter((item) => item.formAccessType !== 'Private').length === 0 ? (
          <p className="mb-4 text-sm text-gray-600">
            {isWidgetConversation
              ? 'Nenhum formulário público vinculado a esta caixa. Formulários privados não podem ser respondidos pelo visitante do widget.'
              : 'Nenhum formulário público vinculado a esta caixa.'}
          </p>
        ) : (
          <div className="mb-4">
            {!isWidgetConversation && (
              <p className="mb-3 text-sm text-gray-600">
                O contato recebe o link público do formulário. As respostas ficam no módulo Formulários e não voltam para esta conversa.
              </p>
            )}
            <label className="mb-1 block text-sm text-gray-700">Formulário</label>
            <select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {linkedForms.filter((item) => item.formAccessType !== 'Private').map((item) => (
                <option key={item.formId} value={item.formId}>
                  {item.formTitle || item.formId}
                  {item.presentOnStart ? ' (início)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsPresentFormOpen(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!selectedFormId || presentingForm || linkedForms.every((item) => item.formAccessType === 'Private')}
            onClick={async () => {
              if (!selectedFormId)
              {
                return;
              }

              setPresentingForm(true);
              try
              {
                const sent = await conversationService.presentForm(conversation.id, selectedFormId);
                if (!isWidgetConversation && sent.status === SupportMessageStatus.Failed)
                {
                  const delivery = parseSupportMessageDelivery(sent.contentAttributes);
                  addToast(
                    delivery.error || 'O link foi registrado, mas o canal não confirmou a entrega.',
                    'error'
                  );
                }
                else if (isWidgetConversation)
                {
                  addToast('Formulário enviado ao visitante', 'success');
                }
                else
                {
                  addToast('Link do formulário enviado', 'success');
                }
                setIsPresentFormOpen(false);
              }
              catch (error)
              {
                addToast(apiErrorMessage(error, 'Não foi possível enviar o formulário'), 'error');
              }
              finally
              {
                setPresentingForm(false);
              }
            }}
          >
            Enviar
          </Button>
        </div>
      </Modal>
    </div>
  );
};
